import React from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://stephanekamwa.com";
const DEFAULT_IMAGE = "/assets/img/mockup-sites.jpg";

function getLanguageMetadata(language) {
    switch ((language || "").split("-")[0]) {
        case "fr":
            return { lang: "fr", locale: "fr_FR" };
        case "de":
            return { lang: "de", locale: "de_DE" };
        case "en":
        default:
            return { lang: "en", locale: "en_US" };
    }
}

function normalizeUrl(path) {
    if (!path) return SITE_URL;
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${SITE_URL}${cleanPath}`;
}

function normalizeImage(image) {
    if (!image) return null;
    if (image.startsWith("http://") || image.startsWith("https://")) return image;
    const cleanPath = image.startsWith("/") ? image : `/${image}`;
    return `${SITE_URL}${cleanPath}`;
}

function Seo({
    title,
    description,
    path = "/",
    image = DEFAULT_IMAGE,
    preloadImage,
    preloadImages = [],
    robots,
    locale,
    lang,
    type = "website",
    keywords,
    siteName,
    imageAlt
}) {
    const { t, i18n } = useTranslation("common");
    const languageMetadata = getLanguageMetadata(i18n.resolvedLanguage || i18n.language);
    const url = normalizeUrl(path);
    const imageUrl = normalizeImage(image);
    const metaTitle = title || t("seo.defaults.title");
    const metaDescription = description || t("seo.defaults.description");
    const metaKeywords = keywords || t("seo.defaults.keywords");
    const metaSiteName = siteName || t("seo.defaults.siteName");
    const metaImageAlt = imageAlt || t("seo.defaults.imageAlt");
    const metaLang = lang || languageMetadata.lang;
    const metaLocale = locale || languageMetadata.locale;
    const preloadList = [preloadImage, ...preloadImages]
        .filter(Boolean)
        .map(normalizeImage)
        .filter(Boolean);

    return (
        <Helmet htmlAttributes={{ lang: metaLang }}>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDescription} />
            <meta name="keywords" content={metaKeywords} />
            {robots && <meta name="robots" content={robots} />}
            <link rel="canonical" href={url} />
            <meta property="og:locale" content={metaLocale} />
            <meta property="og:site_name" content={metaSiteName} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:url" content={url} />
            {imageUrl && <meta property="og:image" content={imageUrl} />}
            {imageUrl && <meta property="og:image:secure_url" content={imageUrl} />}
            {imageUrl && <meta property="og:image:alt" content={metaImageAlt} />}
            <meta name="twitter:card" content={imageUrl ? "summary_large_image" : "summary"} />
            <meta name="twitter:title" content={metaTitle} />
            <meta name="twitter:description" content={metaDescription} />
            {imageUrl && <meta name="twitter:image" content={imageUrl} />}
            {preloadList.map((img) => (
                <link key={img} rel="preload" as="image" href={img} />
            ))}
        </Helmet>
    );
}

export default Seo;
